import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, Search, Shield, MapPin, MessageCircle, Star,
  Dog, Users, Calendar, ShoppingBag, CheckCircle, ArrowRight,
  Sparkles, Camera, Bell, Building2, Coffee, Bed, User, LogIn
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Feature cards data with translation keys
const getFeatures = (t) => [
  {
    icon: Heart,
    title: t('features.pupMatch.title'),
    description: t('features.pupMatch.description'),
    color: "from-[#FF7555] to-[#E5684A]",
    badge: `🔥 ${t('features.pupMatch.badge')}`,
    link: "/puppoto/match"
  },
  {
    icon: Shield,
    title: t('features.verifiedBreeders.title'),
    description: t('features.verifiedBreeders.description'),
    color: "from-[#4E876C] to-[#3D6B56]",
    badge: `✓ ${t('features.verifiedBreeders.badge')}`
  },
  {
    icon: Camera,
    title: t('features.pupSocial.title'),
    description: t('features.pupSocial.description'),
    color: "from-[#F4D35E] to-[#E5C44F]",
    badge: `📸 ${t('features.pupSocial.badge')}`
  },
  {
    icon: Search,
    title: t('features.findServices.title'),
    description: t('features.findServices.description'),
    color: "from-[#6B8EAE] to-[#5A7A96]",
    badge: `🔍 ${t('features.findServices.badge')}`
  },
  {
    icon: Star,
    title: t('features.dogTraining.title'),
    description: t('features.dogTraining.description'),
    color: "from-[#7B68EE] to-[#6A5ACD]",
    badge: `🎓 ${t('features.dogTraining.badge')}`
  },
  {
    icon: Building2,
    title: t('features.doggieDayCare.title'),
    description: t('features.doggieDayCare.description'),
    color: "from-[#9B6B9E] to-[#7D5680]",
    badge: `🏠 ${t('features.doggieDayCare.badge')}`
  },
  {
    icon: Coffee,
    title: t('features.petFriendlyDining.title'),
    description: t('features.petFriendlyDining.description'),
    color: "from-[#D4A373] to-[#BC8C5F]",
    badge: `☕ ${t('features.petFriendlyDining.badge')}`
  },
  {
    icon: MapPin,
    title: t('features.petMotels.title'),
    description: t('features.petMotels.description'),
    color: "from-[#E07A5F] to-[#C96B52]",
    badge: `🏨 ${t('features.petMotels.badge')}`
  },
  {
    icon: Bed,
    title: t('features.petFriendlyStays.title'),
    description: t('features.petFriendlyStays.description'),
    color: "from-[#70AE98] to-[#5D9680]",
    badge: `🏖️ ${t('features.petFriendlyStays.badge')}`
  },
  {
    icon: Bell,
    title: t('features.lostFound.title'),
    description: t('features.lostFound.description'),
    color: "from-[#E8998D] to-[#D4817A]",
    badge: `🚨 ${t('features.lostFound.badge')}`
  },
  {
    icon: ShoppingBag,
    title: t('features.petShop.title'),
    description: t('features.petShop.description'),
    color: "from-[#84A98C] to-[#6B8F73]",
    badge: `🛒 ${t('features.petShop.badge')}`
  }
];

// Stats with translation keys
const getStats = (t) => [
  { value: "50K+", label: t('landing.stats.dogsReady') },
  { value: "2,500+", label: t('landing.stats.verifiedBreeders') },
  { value: "10K+", label: t('landing.stats.successfulMatches') },
  { value: "4.9★", label: t('landing.stats.appRating') }
];

export default function Puppoto() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Generate translated features and stats
  const FEATURES = getFeatures(t);
  const STATS = getStats(t);

  // Check if user is logged in
  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Skip auth check if we have session_id in hash (OAuth callback)
    if (window.location.hash?.includes('session_id=')) {
      setCheckingAuth(false);
      // Process OAuth callback - redirect to profile
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        processOAuthCallback(sessionId);
      }
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/api/puppoto/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (error) {
        // Not logged in, that's fine
      } finally {
        setCheckingAuth(false);
      }
    };
    checkAuth();
  }, []);

  const processOAuthCallback = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/puppoto/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Clear hash
        window.history.replaceState(null, '', '/puppoto');
        toast.success(`Welcome, ${userData.name}!`);
      }
    } catch (error) {
      console.error('Auth error:', error);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/puppoto';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error(t('common.error'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/puppoto/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (res.ok) {
        setSubmitted(true);
        toast.success(t('landing.waitlistSuccess'));
      } else {
        toast.error(t('common.error'));
      }
    } catch (error) {
      // For now, just show success since backend might not be set up
      setSubmitted(true);
      toast.success(t('landing.waitlistSuccess'));
    } finally {
      setSubmitting(false);
    }
  };

  // Register service worker for PWA
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/puppoto-sw.js')
        .then(reg => console.log('Puppoto SW registered'))
        .catch(err => console.log('SW registration failed:', err));
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Puppoto - Find Your Perfect Match 🐕</title>
        <meta name="description" content="The ultimate social platform for dog lovers. Find breeding partners, playdates, verified breeders, and connect with the dog community." />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Fredoka:wght@400;500;600;700&family=Figtree:wght@400;500;600;700&display=swap" rel="stylesheet" />
        {/* PWA Meta Tags */}
        <link rel="manifest" href="/puppoto-manifest.json" />
        <meta name="theme-color" content="#FF7555" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="Puppoto" />
        <link rel="apple-touch-icon" href="/puppoto-icon-192.png" />
      </Helmet>

      <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Figtree', sans-serif" }}>
        {/* Subtle background texture */}
        <div className="fixed inset-0 opacity-[0.02] pointer-events-none" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }} />

        {/* Navigation */}
        <nav className="sticky top-0 z-50 bg-white/75 backdrop-blur-xl backdrop-saturate-150 border-b border-[#EBE8E0]">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <motion.div 
              className="flex items-center gap-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-[#FF7555] to-[#E5684A] rounded-2xl flex items-center justify-center">
                <Dog className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Puppoto
              </span>
            </motion.div>
            
            <div className="hidden md:flex items-center gap-6">
              <a href="/puppoto/match" className="text-[#7A7571] hover:text-[#1A1817] transition font-medium">Match</a>
              <a href="/puppoto/social" className="text-[#7A7571] hover:text-[#1A1817] transition font-medium">Social</a>
              <a href="/puppoto/directory" className="text-[#7A7571] hover:text-[#1A1817] transition font-medium">Directory</a>
              <a href="/puppoto/adopt" className="text-[#7A7571] hover:text-[#1A1817] transition font-medium">Adopt</a>
              <a href="/puppoto/shows" className="text-[#7A7571] hover:text-[#1A1817] transition font-medium">Shows</a>
              <a href="/puppoto/celebrations" className="text-[#F4D35E] hover:text-[#D4B34E] transition font-medium">🎉 Celebrate</a>
              <a href="/puppoto/rainbow-bridge" className="text-purple-500 hover:text-purple-700 transition font-medium">🌈 Memorial</a>
              <a href="/puppoto/partners" className="text-[#FF7555] hover:text-[#E5684A] transition font-medium">Partners</a>
            </div>

            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="compact" />
              
              {/* Auth Button - Show Profile or Login */}
              {!checkingAuth && (
                user ? (
                  <Button 
                    className="bg-[#4E876C] hover:bg-[#3D6B56] text-white rounded-full px-4 font-semibold shadow-lg hidden sm:flex items-center gap-2"
                    onClick={() => navigate('/puppoto/profile')}
                    data-testid="profile-button"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                    ) : (
                      <User className="w-4 h-4" />
                    )}
                    {user.name?.split(' ')[0]}
                  </Button>
                ) : (
                  <Button 
                    className="bg-white hover:bg-[#F5F3EE] text-[#1A1817] rounded-full px-4 font-semibold border border-[#EBE8E0] hidden sm:flex items-center gap-2"
                    onClick={handleGoogleLogin}
                    data-testid="login-button"
                  >
                    <LogIn className="w-4 h-4" />
                    Sign In
                  </Button>
                )
              )}
              
              <Button 
                className="bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-full px-6 font-semibold shadow-[0_8px_30px_rgba(255,117,85,0.3)] hidden sm:flex"
                onClick={() => document.getElementById('waitlist').scrollIntoView({ behavior: 'smooth' })}
              >
                {t('common.joinWaitlist')}
              </Button>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative overflow-hidden pt-12 pb-20 md:pt-20 md:pb-32">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left: Content */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 bg-[#4E876C]/10 text-[#4E876C] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <Sparkles className="w-4 h-4" />
                  {t('landing.tagline')}
                </div>
                
                <h1 
                  className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#1A1817] leading-[1.1] tracking-tight mb-6"
                  style={{ fontFamily: "'Fredoka', sans-serif" }}
                  dangerouslySetInnerHTML={{ 
                    __html: t('landing.heroTitle').replace('Perfect', '<span class="text-[#FF7555]">Perfect</span>').replace('Pup Match', '<br/>Pup Match')
                  }}
                />
                
                <p className="text-xl text-[#7A7571] mb-8 max-w-lg leading-relaxed">
                  {t('landing.heroSubtitle')}
                </p>

                {/* Waitlist Form */}
                <div id="waitlist" className="mb-8">
                  {!submitted ? (
                    <form onSubmit={handleWaitlist} className="flex gap-3 max-w-md">
                      <Input
                        type="email"
                        placeholder={t('landing.emailPlaceholder')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-14 bg-white border-[#EBE8E0] rounded-2xl text-lg px-5 flex-1 focus:ring-2 focus:ring-[#FF7555] focus:border-transparent"
                        data-testid="waitlist-email-input"
                      />
                      <Button 
                        type="submit"
                        disabled={submitting}
                        className="h-14 bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-2xl px-8 font-semibold text-lg shadow-[0_8px_30px_rgba(255,117,85,0.3)] hover:shadow-[0_12px_40px_rgba(255,117,85,0.4)] transition-all active:scale-95"
                        data-testid="waitlist-submit-button"
                      >
                        {submitting ? '...' : t('landing.joinButton')}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </form>
                  ) : (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center gap-3 bg-[#4E876C]/10 text-[#4E876C] px-6 py-4 rounded-2xl max-w-md"
                    >
                      <CheckCircle className="w-6 h-6" />
                      <span className="font-semibold">{t('landing.waitlistSuccess')} 🐕</span>
                    </motion.div>
                  )}
                  <p className="text-sm text-[#7A7571] mt-3">
                    {t('landing.waitlistCount', { count: '5,000' })}
                  </p>
                </div>

                {/* Trust Badges */}
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 text-sm text-[#7A7571]">
                    <Shield className="w-5 h-5 text-[#4E876C]" />
                    <span>{t('landing.trustBadges.vetVerified')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#7A7571]">
                    <Heart className="w-5 h-5 text-[#FF7555]" />
                    <span>{t('landing.trustBadges.healthGuaranteed')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#7A7571]">
                    <MapPin className="w-5 h-5 text-[#F4D35E]" />
                    <span>{t('landing.trustBadges.australiaWide')}</span>
                  </div>
                </div>
              </motion.div>

              {/* Right: Meet Toby - CBO Hero */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative flex justify-center"
              >
                {/* Toby Hero Card */}
                <div className="relative">
                  <motion.div 
                    className="relative w-[380px] rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.3)]"
                    whileHover={{ y: -5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_tradie-leads-1/artifacts/qrfhbe4b_Our%20Obi%20Puppoto%20ModelIMG_1916.jpg"
                      alt="Toby - Chief Barking Officer"
                      className="w-full h-[480px] object-cover"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    
                    {/* CBO Badge */}
                    <div className="absolute top-4 right-4">
                      <motion.div 
                        className="bg-[#FF7555] text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg"
                        animate={{ rotate: [0, 3, -3, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        CBO
                      </motion.div>
                    </div>
                    
                    {/* Info */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-[#4E876C] text-white text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1">
                          <Shield className="w-3 h-3" /> Verified Good Boy
                        </span>
                      </div>
                      <h3 className="text-white text-3xl font-bold mb-1" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                        Meet Toby
                      </h3>
                      <p className="text-white/90 text-lg font-medium">Chief Barking Officer</p>
                      <p className="text-white/70 text-sm mt-1">Pomchi • Official Mascot</p>
                    </div>
                  </motion.div>

                  {/* Floating Match Notification */}
                  <motion.div 
                    className="absolute -left-12 top-16 bg-white rounded-2xl p-4 shadow-[0_8px_30px_rgba(0,0,0,0.12)]"
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#FF7555]">
                        <img src="https://customer-assets.emergentagent.com/job_tradie-leads-1/artifacts/qrfhbe4b_Our%20Obi%20Puppoto%20ModelIMG_1916.jpg" alt="Toby" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1A1817]">It's a Match! 🎉</p>
                        <p className="text-sm text-[#7A7571]">Toby liked you back</p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Floating Badge */}
                  <motion.div 
                    className="absolute -right-8 bottom-28 bg-[#4E876C] text-white rounded-2xl px-5 py-3 shadow-lg"
                    animate={{ y: [0, 8, 0] }}
                    transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
                  >
                    <div className="flex items-center gap-2">
                      <Heart className="w-5 h-5" fill="white" />
                      <span className="font-semibold">5,000+ Matches</span>
                    </div>
                  </motion.div>

                  {/* Meet the Team Link */}
                  <motion.a
                    href="/puppoto/team"
                    className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white text-[#1A1817] rounded-full px-6 py-3 shadow-lg font-semibold text-sm hover:bg-[#FF7555] hover:text-white transition-colors flex items-center gap-2"
                    whileHover={{ scale: 1.05 }}
                  >
                    <Users className="w-4 h-4" />
                    Meet the Team
                  </motion.a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#1A1817] py-12">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {STATS.map((stat, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="text-4xl md:text-5xl font-bold text-[#FF7555]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    {stat.value}
                  </div>
                  <div className="text-[#7A7571] mt-2">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 md:py-32">
          <div className="max-w-6xl mx-auto px-4">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block bg-[#FF7555]/10 text-[#FF7555] px-4 py-2 rounded-full text-sm font-semibold mb-4">
                {t('features.title').toUpperCase().split(' ')[0]}
              </span>
              <h2 
                className="text-4xl md:text-5xl font-bold text-[#1A1817] mb-4"
                style={{ fontFamily: "'Fredoka', sans-serif" }}
              >
                {t('features.title')}
              </h2>
              <p className="text-xl text-[#7A7571] max-w-2xl mx-auto">
                {t('features.subtitle')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((feature, idx) => {
                const CardWrapper = feature.link ? 'a' : 'div';
                const cardProps = feature.link ? { href: feature.link } : {};
                
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <CardWrapper 
                      {...cardProps}
                      className={`block group bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-[#EBE8E0] hover:shadow-[0_20px_60px_rgba(0,0,0,0.08)] hover:border-[#FF7555]/30 transition-all duration-300 ${feature.link ? 'cursor-pointer' : ''}`}
                    >
                      <div className={`w-14 h-14 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                        <feature.icon className="w-7 h-7 text-white" />
                      </div>
                      <span className={`text-xs font-semibold uppercase tracking-wider ${feature.link ? 'text-[#FF7555]' : 'text-[#7A7571]'}`}>
                        {feature.badge}
                      </span>
                      <h3 className="text-xl font-bold text-[#1A1817] mt-2 mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                        {feature.title}
                      </h3>
                      <p className="text-[#7A7571] leading-relaxed">{feature.description}</p>
                      {feature.link && (
                        <div className="mt-4 text-[#FF7555] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                          Try it now <ArrowRight className="w-4 h-4" />
                        </div>
                      )}
                    </CardWrapper>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="py-20 bg-gradient-to-br from-[#FF7555]/5 to-[#F4D35E]/5">
          <div className="max-w-6xl mx-auto px-4">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block bg-[#4E876C]/10 text-[#4E876C] px-4 py-2 rounded-full text-sm font-semibold mb-4">
                {t('howItWorks.title').toUpperCase().split(' ').slice(0, 2).join(' ')}
              </span>
              <h2 
                className="text-4xl md:text-5xl font-bold text-[#1A1817] mb-4"
                style={{ fontFamily: "'Fredoka', sans-serif" }}
              >
                {t('howItWorks.title')}
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                { step: "1", title: t('howItWorks.step1.title'), desc: t('howItWorks.step1.description'), icon: Dog },
                { step: "2", title: t('howItWorks.step2.title'), desc: t('howItWorks.step2.description'), icon: Heart },
                { step: "3", title: t('howItWorks.step3.title'), desc: t('howItWorks.step3.description'), icon: MessageCircle },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.15 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="relative inline-block mb-6">
                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
                      <item.icon className="w-10 h-10 text-[#FF7555]" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#FF7555] text-white rounded-full flex items-center justify-center font-bold text-sm">
                      {item.step}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1817] mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    {item.title}
                  </h3>
                  <p className="text-[#7A7571]">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* For Breeders CTA */}
        <section id="breeders" className="py-20">
          <div className="max-w-6xl mx-auto px-4">
            <div className="bg-gradient-to-br from-[#4E876C] to-[#3D6B56] rounded-[2rem] p-8 md:p-12 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="inline-block bg-white/20 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                    {t('forBreeders.title').toUpperCase().split(' ').slice(0, 2).join(' ')}
                  </span>
                  <h2 
                    className="text-3xl md:text-4xl font-bold mb-4"
                    style={{ fontFamily: "'Fredoka', sans-serif" }}
                  >
                    {t('forBreeders.title')}
                  </h2>
                  <p className="text-white/80 text-lg mb-6">
                    {t('forBreeders.subtitle')}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {[t('forBreeders.benefits.badge'), t('forBreeders.benefits.litters'), t('forBreeders.benefits.leads'), t('forBreeders.benefits.health')].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-[#F4D35E]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className="bg-white text-[#4E876C] hover:bg-white/90 rounded-full px-8 h-12 font-semibold">
                    {t('forBreeders.cta')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
                <div className="hidden md:block">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_tradie-leads-1/artifacts/92iyvmuc_Tobi%20CBO%20Puppoto.jpg"
                    alt="Veterinarian with dog"
                    className="rounded-2xl shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* $PUPP Token Teaser Section */}
        <section className="py-20 bg-gradient-to-br from-[#1A1817] via-[#2D2926] to-[#1A1817] relative overflow-hidden">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#FF7555]/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-[#F4D35E]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          </div>
          
          <div className="max-w-4xl mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              {/* Coming Soon Badge */}
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#F4D35E]/20 to-[#FF7555]/20 border border-[#F4D35E]/30 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <span className="w-2 h-2 bg-[#F4D35E] rounded-full animate-pulse" />
                <span className="text-[#F4D35E]">COMING SOON</span>
              </div>
              
              {/* Token Title */}
              <h2 
                className="text-5xl md:text-7xl font-bold text-white mb-4"
                style={{ fontFamily: "'Fredoka', sans-serif" }}
              >
                <span className="bg-gradient-to-r from-[#F4D35E] via-[#FF7555] to-[#F4D35E] bg-clip-text text-transparent">
                  $PUPP
                </span>
              </h2>
              <p className="text-2xl text-white/80 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                The Puppoto Token
              </p>
              <p className="text-[#7A7571] text-lg max-w-xl mx-auto mb-8">
                Earn, spend, and trade within the Puppoto ecosystem. Boost your pup's profile, 
                unlock premium features, and be part of the community.
              </p>
              
              {/* Token Features */}
              <div className="grid md:grid-cols-3 gap-4 mb-10">
                {[
                  { icon: '🚀', title: 'Profile Boosts', desc: 'Get more visibility for your pup' },
                  { icon: '⭐', title: 'Super Likes', desc: 'Stand out from the pack' },
                  { icon: '🏆', title: 'Earn Rewards', desc: 'Active users earn $PUPP' },
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5"
                  >
                    <span className="text-3xl mb-2 block">{item.icon}</span>
                    <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                    <p className="text-[#7A7571] text-sm">{item.desc}</p>
                  </motion.div>
                ))}
              </div>
              
              {/* Waitlist for Token */}
              <div className="max-w-md mx-auto">
                <p className="text-white/60 text-sm mb-3">Be the first to know when $PUPP launches</p>
                <Button 
                  className="w-full h-14 bg-gradient-to-r from-[#F4D35E] to-[#FF7555] hover:opacity-90 text-[#1A1817] rounded-2xl font-bold text-lg shadow-[0_0_40px_rgba(244,211,94,0.3)]"
                  onClick={() => document.getElementById('waitlist').scrollIntoView({ behavior: 'smooth' })}
                >
                  Join the $PUPP Waitlist 🐕
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-[#1A1817]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 
                className="text-4xl md:text-5xl font-bold text-white mb-4"
                style={{ fontFamily: "'Fredoka', sans-serif" }}
                dangerouslySetInnerHTML={{ 
                  __html: t('cta.title').replace('Perfect Match', '<span class="text-[#FF7555]">Perfect Match</span>')
                }}
              />
              <p className="text-xl text-[#7A7571] mb-8">
                {t('cta.subtitle')}
              </p>
              
              {!submitted ? (
                <form onSubmit={handleWaitlist} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                  <Input
                    type="email"
                    placeholder={t('landing.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-14 bg-white/10 border-white/20 text-white rounded-2xl text-lg px-5 flex-1 placeholder:text-white/50"
                  />
                  <Button 
                    type="submit"
                    disabled={submitting}
                    className="h-14 bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-2xl px-8 font-semibold text-lg"
                  >
                    {submitting ? '...' : t('common.joinWaitlist')}
                  </Button>
                </form>
              ) : (
                <div className="flex items-center justify-center gap-3 bg-[#4E876C]/20 text-[#4E876C] px-6 py-4 rounded-2xl max-w-md mx-auto">
                  <CheckCircle className="w-6 h-6" />
                  <span className="font-semibold">{t('landing.waitlistSuccess')} 🐕</span>
                </div>
              )}
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#1A1817] border-t border-white/10 py-8">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#FF7555] to-[#E5684A] rounded-xl flex items-center justify-center">
                  <Dog className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-white" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  Puppoto
                </span>
              </div>
              <p className="text-[#7A7571] text-sm">
                {t('footer.copyright', { year: new Date().getFullYear() })} {t('footer.madeWith')}
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
